import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, setPersistence, browserLocalPersistence, browserSessionPersistence, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getAnalytics, isSupported, logEvent } from "firebase/analytics";

const firebaseConfig = {
  apiKey: "AIzaSyAKJEn8_MfG41JFLu4BzEwE_D1Jlol1Igk",
  // Keep the original Firebase authDomain
  authDomain: "beautyfinancetracker.firebaseapp.com",
  projectId: "beautyfinancetracker",
  storageBucket: "beautyfinancetracker.firebasestorage.app",
  messagingSenderId: "303036808030",
  appId: "1:303036808030:web:3131ef5e8ea409bc7b6dcf",
  measurementId: "G-JEFQ8LZR72"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const analyticsPromise = (() => {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  return isSupported()
    .then((supported) => (supported ? getAnalytics(app) : null))
    .catch((error) => {
      console.warn("Analytics not supported:", error);
      return null;
    });
})();

// Configure auth for your custom domain
const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  auth_domain: 'app.70lives.com'
});

const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1');

// Using production Firebase Functions
console.log('Using production Firebase Functions');

/**
 * Apply the desired auth persistence and remember the preference locally.
 * When rememberMe is true, Firebase uses local persistence (survives restarts);
 * otherwise it falls back to session-only persistence.
 */
export const setAuthPersistence = async (rememberMe = true) => {
  const target = rememberMe ? browserLocalPersistence : browserSessionPersistence;
  try {
    await setPersistence(auth, target);
    try { localStorage.setItem('remember_me', rememberMe ? 'true' : 'false'); } catch {}
    console.log(`Auth persistence set to ${rememberMe ? 'LOCAL' : 'SESSION'}`);
  } catch (error) {
    console.error('Error setting auth persistence:', error);
  }
};

// Apply initial persistence based on stored preference (default to remember).
(() => {
  const shouldRemember = (() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = localStorage.getItem('remember_me');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  })();
  setAuthPersistence(shouldRemember);
})();

// Enhanced debug listener with more details
onAuthStateChanged(auth, (user) => {
  if (user) {
    console.log('Firebase Auth: User is signed in:', user.email);
    console.log('Firebase Auth: Auth token available:', !!user.accessToken);
    console.log('Firebase Auth: User UID:', user.uid);

    // Store auth state in localStorage as backup
    localStorage.setItem('firebase_auth_user', JSON.stringify({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      timestamp: Date.now()
    }));
  } else {
    console.log('Firebase Auth: No user is signed in');

    // Try to restore from Google auth if available
    import('./utils/authSync').then(({ restoreFirebaseAuthFromGoogle }) => {
      restoreFirebaseAuthFromGoogle().then((restoredUser) => {
        if (restoredUser) {
          console.log('Firebase auth restored from stored state');
        } else {
          console.log('Could not restore Firebase auth');
          localStorage.removeItem('firebase_auth_user');
          localStorage.removeItem('firebase_auth_timestamp');
        }
      }).catch((error) => {
        console.error('Error restoring Firebase auth:', error);
        localStorage.removeItem('firebase_auth_user');
        localStorage.removeItem('firebase_auth_timestamp');
      });
    });
  }
});

// Listen for custom auth restoration events
window.addEventListener('firebase-auth-restored', (event) => {
  const user = event.detail;
  console.log('Custom Firebase auth restored:', user.email);

  // Manually trigger auth state change in the app
  if (window.updateAppAuthState) {
    window.updateAppAuthState(user);
  }
});

export const trackFeatureUsage = async (feature, params = {}) => {
  if (!feature) return;
  try {
    const analytics = await analyticsPromise;
    if (!analytics) return;
    logEvent(analytics, "feature_used", { feature, ...params });
  } catch (error) {
    console.warn("Analytics event failed:", error);
  }
};

export { app, auth, db, functions, provider };
